// Angular
import { BehaviorSubject, Observable, of, takeUntil } from 'rxjs';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

// Libraries
import { Chart } from 'angular-highcharts';
import { Options, PointClickEventObject, SeriesEventsOptionsObject, SeriesSplineOptions, XAxisOptions, YAxisOptions } from 'highcharts';

import { forkJoin } from 'rxjs/internal/observable/forkJoin';
import { AnnotationsOptions } from 'highcharts/highcharts.src';

import { TranslateService } from '@ngx-translate/core';

// Models
import { Job } from '../../../models';
import { UnitPeriodChannelDto } from '../../../../channel/models/unit-period-channel-dto.model';
import { CustomChartInitModel, CustomChartModel, DefaultSettingsForCustomChartView } from '../models/custom-chart-init-model';
import { CustomChartSettingsType, ReductionMethod, SaturationMode } from '../dialogs/set-attachment-options/models/types';
import { JobReturnCache } from '../../../models/job-return-cache.model';
import { UserUnitPeriodChannelDto } from '../../../../channel/models/user-unit-period-channel-dto';
import { AppCleanup, Colors } from '../../../../../shared/models';
import { JobAttachmentViewModel } from '../../../models/job-attachment.model';
import { ChannelSettingsModel, ChartSettingsModel } from '../models/custom-charts.model';
import { SampleDataSaturationModel } from '../../../models/sample-data-saturation-model';
import { SaveCustomChartAttachmentDialogResponse } from '../dialogs/set-attachment-options/models/save-custom-chart-attachment-dialog-response';

// Helpers
import { DateHelper, HighchartsHelper, UtilityHelper } from '../../../../../shared/helpers';

// Services
import { CustomChartsService } from '../../../services/custom-charts.service';
import { JobService } from '../../../services/job.service';
import { JobDataService } from '../../../../../shared/workers/job-data.service';
import { CoreService } from '../../../../../shared/services';
import { TimezoneService } from '../../../services/timezone.service';
import { SpinnerService } from '../../../../../shared/components/spinner/services/spinner.service';

// Enums
import { StatusType } from '../../../../../shared/enums';

@Injectable()
export class CustomChartsInitService extends AppCleanup {
	constructor(
		private jobService: JobService,
		private jobDataService: JobDataService,
		private customChartService: CustomChartsService,
		private timezoneService: TimezoneService,
		private router: Router,
		private spinnerService: SpinnerService,
		private coreService: CoreService,
		private translateService: TranslateService
	) {
		super();
	}

	private readonly defaultChartSettings = {
		'auto-gen-pressure-test': {
			title: 'Pressure Test',
			combineUnits: true,
			channelLabels: ['Wellhead Pressure', 'Circulating Pressure'],
		},
	};
	private readonly defaultSaturationSettings: SampleDataSaturationModel = {
		secondSpan: undefined,
		saturationLimit: 1000,
		reductionMethod: 2,
		saturationMode: 'max-points',
	};

	private readonly newAnnotation: AnnotationsOptions = {
		labels: [
			{
				point: {
					x: 0,
					y: 0,
					xAxis: 0,
					yAxis: 0,
				},
				text: '',
			},
		],
		visible: true,
		labelOptions: {
			backgroundColor: 'white',
			borderColor: '#e0e0e0',
			padding: 12,
			allowOverlap: true,
			borderRadius: 4,
			style: {
				fontSize: '11px',
				width: 125,
				color: '',
			},
		},
	};
	job: Job;
	protected jobVar: BehaviorSubject<Job> = new BehaviorSubject<Job>(null);
	job$ = this.jobVar.asObservable();
	private minDate: any;
	private maxDate: any;
	dateRange: BehaviorSubject<{ startDate: Date; endDate: Date }> = new BehaviorSubject(null);
	private unitPeriods: JobReturnCache[];
	private chartSettings: ChartSettingsModel;
	private companyChannels: UnitPeriodChannelDto[];
	private customChartModel: BehaviorSubject<CustomChartModel> = new BehaviorSubject(undefined);
	private customChartSettingsType: CustomChartSettingsType = 'for-custom-charts-view';
	private callingComponent: any;
	private initialAnnotations: any[]; // comments/annotations to be loaded when chart first instantiated
	private startDate?: Date;
	private endDate?: Date;
	private rawMinDate: Date;
	private rawMaxDate: Date;

	getCustomChartModel(customChartInitModel: CustomChartInitModel): Observable<CustomChartModel> {
		this.customChartSettingsType = customChartInitModel.customChartSettingsType;
		this.initialAnnotations = customChartInitModel?.annotations || undefined;
		this.startDate = customChartInitModel?.startDate || undefined;
		this.endDate = customChartInitModel?.endDate || undefined;
		this.callingComponent = customChartInitModel?.callingComponent || undefined;
		this.chartSettings = customChartInitModel?.chartSettings || undefined;
		this.init(customChartInitModel);
		return this.customChartModel;
	}

	syncChartRef(chart: Chart) {
		const customChartModel: CustomChartModel = this.customChartModel?.getValue();
		customChartModel.chart = chart;
		this.customChartModel.next({ ...customChartModel });
	}

	resetOriginalChartSettings(chartSettings: ChartSettingsModel = this.chartSettings, companyChannels: UnitPeriodChannelDto[] = this.companyChannels, userUnitPeriodChannels: UserUnitPeriodChannelDto[]): ChartSettingsModel {
		// Resetting chart, matching it to user unit period channel.
		chartSettings.channelSettings = [];

		for (let i: number = 0; i < companyChannels.length; i++) {
			const channelSetting: ChannelSettingsModel = new ChannelSettingsModel();
			channelSetting.id = companyChannels[i].id;
			const userChannel: UserUnitPeriodChannelDto = userUnitPeriodChannels.find((channel: UserUnitPeriodChannelDto) => channel.unitPeriodChannelId === companyChannels[i].id);

			if (userChannel) {
				channelSetting.label = userChannel.unitPeriodChannel.channel.label;
				channelSetting.colorHex = userChannel.colorHex;
				channelSetting.showGraph = userChannel.showGraph;
				channelSetting.lineWeight = userChannel.lineWeight;
				channelSetting.minY = userChannel.minY;
				channelSetting.maxY = userChannel.maxY;
			} else {
				channelSetting.label = companyChannels[i].channel.label;
				channelSetting.colorHex = companyChannels[i].colorHex;
				channelSetting.showGraph = true;
				channelSetting.lineWeight = companyChannels[i].lineWeight;
				channelSetting.minY = companyChannels[i].channel.minY;
				channelSetting.maxY = companyChannels[i].channel.maxY;
			}

			channelSetting.units = companyChannels[i].channel.units;

			chartSettings.channelSettings.push(channelSetting);
		}

		this.customChartService.updateChartSettings(chartSettings);

		return chartSettings;
	}

	buildChart(chartSettings: ChartSettingsModel = this.chartSettings) {
		const yAxis: YAxisOptions[] = [];
		const seriesArray = [];
		let minDate: number = null;
		let maxDate: number = null;

		// Import comment axis.
		yAxis.push({
			id: '0',
			visible: false,
			title: {
				text: null,
			},
			showEmpty: false,
			tickInterval: 1,
		});

		const chartOptions: Options = HighchartsHelper.getSplinePlotOptions();

		const chartColors: Array<string> = [...Colors.ChartDefaults];
		chartOptions.time = {
			timezoneOffset: this.job.utcDateTimeOffset * -60 || this.timezoneService.getCurrentTimeZone() * -60,
			// useUTC: false // Adding this to fix the issue of x-axis ticks being 1 hour behind. https://stackoverflow.com/questions/18812030/highcharts-time-off-by-1-hour
		};
		chartOptions.plotOptions.spline.tooltip = {};

		chartOptions.legend = {
			layout: 'horizontal',
			align: 'center',
			verticalAlign: 'bottom',
			itemMarginTop: 1,
			itemMarginBottom: 1,
			padding: 0,
			itemStyle: {
				fontSize: '10px',
				fontWeight: 'bold',
				color: '',
			},
			itemHoverStyle: {
				color: '',
			},
		};
		chartOptions.series = [];

		// Converting for consistent date/time display with x-axis values.
		const jobUtcDateTimeOffset = this.job.utcDateTimeOffset;
		chartOptions.tooltip = {
			headerFormat: '',
			pointFormatter: function () {
				const displayDateArray: string[] = DateHelper.formatDateInTimezone(this.x, jobUtcDateTimeOffset).split(' ');
				const date: string = displayDateArray[0];
				const time: string = displayDateArray[1];
				return `<span style="font-weight: 600; color: #8C8C8C;">${date}</span>&nbsp;<span style="color: #8C8C8C; font-weight: 500;">${time}</span><br /><span style="color: #8C8C8C">${this.y}</span>`;
			},
		};

		if (this?.callingComponent) {
			chartOptions.plotOptions.series = {
				point: {
					events: {
						click: (event: PointClickEventObject) => this.callingComponent['displayAddCommentDialog'](event.point.options.x, event.point.options.y, event.point.series.index),
					},
				},
			};
		} else {
			chartOptions.plotOptions.series.animation = false;
		}
		(chartOptions.xAxis as XAxisOptions).title.text = '';
		(chartOptions.xAxis as XAxisOptions).type = 'datetime';
		(chartOptions.xAxis as XAxisOptions).labels.style.color = '';
		chartOptions.colors = chartColors;
		const initialAnnotations = this.initialAnnotations;

		chartOptions.chart.events = {
			load() {
				let thisChart = this;
				if (initialAnnotations?.length) {
					// Create deep copies to prevent Highcharts from mutating the originals
					initialAnnotations.forEach(async (annotation, index) => {
						const annotationCopy = {
							...annotation,
							id: UtilityHelper.generateUuid(), // Generate new ID
							labels: annotation.labels.map((label) => ({
								...label,
								point: { ...label.point },
							})),
						};
						thisChart.addAnnotation(annotationCopy);
						await UtilityHelper.sleep(100);

						// Add visual marker dots for each annotation
						annotation.labels.forEach((label) => {
							if (label.point) {
								// Add marker dot at the annotation location
								const xValue = label.point.x;
								const yValue = label.point.y;
								const seriesIndex = label.point.yAxis || 0;

								// Determine marker positioning (same logic as main component)
								const isImportedComment = seriesIndex === 0;
								const markerYAxis = isImportedComment ? 0 : seriesIndex;
								const markerYValue = isImportedComment && thisChart.yAxis?.[0] ? thisChart.yAxis[0].min : yValue;

								// Create unique series ID for each yAxis
								const seriesId = `comment-markers-axis-${markerYAxis}`;

								// Find or create the comment markers series

								let commentSeries = thisChart.series?.find((series) => series.options.id === seriesId);

								if (!commentSeries && thisChart && thisChart.addSeries && thisChart.series) {
									// Create the comment markers series if it doesn't exist
									commentSeries = thisChart.addSeries(
										{
											id: seriesId,
											name: 'Comments',
											type: 'scatter',
											data: [],
											color: '#808080',
											marker: {
												radius: 4,
												fillColor: '#808080',
												lineColor: '#000000',
												lineWidth: 1,
												symbol: 'circle',
												enabled: true,
												states: {
													hover: {
														enabled: false,
														fillColor: '#808080',
														lineColor: '#000000',
														radius: 4,
													},
													select: {
														enabled: false,
													},
													inactive: {
														opacity: 1,
													},
												},
											},
											yAxis: markerYAxis,
											showInLegend: false,
											enableMouseTracking: false,
											zIndex: 1000,
											visible: true,
											opacity: 1,
											stickyTracking: false,
											states: {
												hover: {
													enabled: false,
												},
												inactive: {
													opacity: 1,
												},
											},
										},
										false
									);
								}

								// Add the marker point
								if (commentSeries && commentSeries.addPoint) {
									commentSeries.addPoint(
										{
											x: xValue,
											y: markerYValue,
											marker: {
												fillColor: '#808080',
												lineColor: '#000000',
												radius: 4,
											},
										},
										false
									);
								}
							}
						});

						// Redraw chart once after all annotations and markers are added
						if (thisChart && thisChart.series && typeof thisChart.redraw === 'function') {
							setTimeout(() => {
								try {
									thisChart.redraw();
								} catch (e) {
									console.warn('Chart redraw failed:', e);
								}
							}, 300);
						}
					});
				}
			},
			selection: (event: any) => this.selectPointsByDrag(event),
		};

		let yAxisMin: number = 0;
		let yAxisMax: number = 0;

		// Update chart settings for channels, based on type/context.
		if (this.customChartSettingsType !== 'for-custom-charts-view') {
			chartSettings.channelSettings = this.getChannelSettingsByChartGenerationType(chartSettings, this.customChartSettingsType);
		}

		// Update channels.
		for (let i: number = 0; i < this.companyChannels.length; i++) {
			let series: SeriesSplineOptions = {} as SeriesSplineOptions;
			series.cursor = 'pointer';
			series.events = {} as SeriesEventsOptionsObject;
			if (this?.callingComponent) {
				series.events.legendItemClick = () => {
					this.callingComponent['seriesLegendToggle'](i);
				};
			}
			series.id = String(i);
			series.turboThreshold = 10000;
			series.yAxis = Number(i + 1);
			series.color = chartSettings.channelSettings[i].colorHex;
			series.visible = chartSettings.channelSettings[i].showGraph;
			series.lineWidth = chartSettings.channelSettings[i].lineWeight;
			series.name = `${this.companyChannels[i].channel.label} (${this.companyChannels[i].channel.units})`;
			series.type = undefined;
			series.showInLegend = chartSettings.channelSettings[i].showGraph;
			series.marker = {
				enabled: false,
				symbol: 'circle',
				radius: 2,
			};
			series.data = [];

			const unitPeriod: JobReturnCache = this.unitPeriods.find((unitPeriod: JobReturnCache) => unitPeriod.unitPeriodId === this.companyChannels[i].unitPeriodId);
			const channelIndex: number = unitPeriod.channelIds.indexOf(this.companyChannels[i].channelId);

			for (let datum of unitPeriod.data) {
				const value = datum.data[channelIndex];
				if (value > yAxisMax) {
					yAxisMax = value;
				}

				if (value < yAxisMin) {
					yAxisMin = value;
				}

				let timestamp: number = datum.unix * 1000;

				let newDatum = {
					name: DateHelper.format(this.timezoneService.convertDateToJobTimezone(datum.unix * 1000, this.job.utcDateTimeOffset), 'MMM D YYYY HH:mm:ss'),
					y: Math.round(value * 100) / 100,
					x: timestamp,
				};

				if (!minDate || timestamp < minDate) {
					minDate = timestamp;
				}

				if (!maxDate || timestamp > maxDate) {
					maxDate = timestamp;
				}

				series.data.push(newDatum);
			}

			if (!minDate || !maxDate) {
				this.router.navigate([`/job/${this.job.id}`]);
				this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
				const message = `${this.translateService.instant('MODULE.JOB.CUSTOM_CHART.MIN_OR_MAX_DATE_INVALID')}`;
				this.coreService.toast({ message: message, type: StatusType.DANGER });
				return;
			}

			this.minDate = this.timezoneService.convertDateToJobTimezone(minDate, this.job.utcDateTimeOffset);
			this.maxDate = this.timezoneService.convertDateToJobTimezone(maxDate, this.job.utcDateTimeOffset);

			// Store raw UTC timestamps for date picker (to match chart x-axis)
			this.rawMinDate = new Date(minDate);
			this.rawMaxDate = new Date(maxDate);

			seriesArray.push(series);

			const channelSetting: ChannelSettingsModel = chartSettings.channelSettings[i];
			let linkedTo: number;
			if ('linkedTo' in channelSetting && channelSetting.linkedTo !== undefined && channelSetting.linkedTo !== null) {
				linkedTo = channelSetting.linkedTo + 1;
			}

			const yAxisOptions = {
				id: String(Number(series.id + 1)),
				showEmpty: false,
				// margin: 10,
				visible: !chartSettings.channelSettings[i].combined,
				labels: {
					style: {
						color: chartSettings.channelSettings[i].colorHex,
					},
					rotation: -90,
					padding: 0,
					distance: 0,
				},
				title: {
					text: null,
				},
				// Using softMin, because otherwise axis don't hide when clicking the legend.
				softMin: chartSettings.channelSettings[i].minY,
				// Using softMax, because otherwise axis don't hide when clicking the legend.
				softMax: chartSettings.channelSettings[i].maxY,
			};

			if (linkedTo !== undefined && linkedTo !== null) {
				yAxisOptions['linkedTo'] = linkedTo;
			}

			yAxis.push(yAxisOptions);
		}

		chartOptions.series = seriesArray;
		yAxis[0].min = yAxisMin;
		yAxis[0].max = yAxisMax;
		chartOptions.yAxis = yAxis;

		const chart: Chart = new Chart(chartOptions);
		this.redrawAnnotations(chart, chartSettings, chartOptions.annotations);
		this.customChartModel.next({
			chart,
			chartSettings,
			minDate: this.minDate,
			maxDate: this.maxDate,
			rawMinDate: this.rawMinDate,
			rawMaxDate: this.rawMaxDate,
			companyChannels: this.companyChannels,
			unitPeriods: this.unitPeriods,
		});
	}

	redrawAnnotations(chart: Chart, chartSettings: ChartSettingsModel, annotations: any[], shouldReload: boolean = false) {
		if (chart?.ref) {
			for (let i: number = 0; i < annotations.length; i++) {
				for (let j: number = 0; j < annotations[i].length; j++) {
					chart.ref.removeAnnotation(annotations[i][j].id);
					if (chartSettings.channelSettings[i].showGraph) {
						setTimeout(() => {
							chart.ref.addAnnotation(annotations[i][j]);
						}, 1);
					}
				}

				if (shouldReload) {
					const customChartModel: CustomChartModel = this.customChartModel.getValue();
					customChartModel.chart = chart;
					this.customChartModel.next({ ...customChartModel });
				}
			}
		}
	}

	updateExtremes(startDate: Date, endDate: Date) {
		const customChartModel: CustomChartModel = this.customChartModel.getValue();

		if (!customChartModel?.chart?.ref?.xAxis || !startDate || !endDate) return;

		startDate.setSeconds(0);
		endDate.setSeconds(0);

		let startDateMillis: number = startDate.getTime();
		let endDateMillis: number = endDate.getTime();

		if (customChartModel.chart.ref.xAxis[0].getExtremes().min !== startDateMillis || customChartModel.chart.ref.xAxis[0].getExtremes().max !== endDateMillis) {
			customChartModel.chart.ref.xAxis[0].setExtremes(startDateMillis, endDateMillis, true);

			this.customChartModel.next({ ...customChartModel });
		}
	}

	getDefaultSettingsForCustomChartsView(): DefaultSettingsForCustomChartView {
		return {
			userUnitPeriodChannels: this.jobService.findUserUnitPeriodChannelsForJob(this.job.key),
			companyChannels: this.jobService.findUnitPeriodChannelsForJob(this.job.id),
			chartSettings: this.buildBaseChartSettingsForCustomChartsView(this.job.id),
		};
	}

	saveToAttachments(response: SaveCustomChartAttachmentDialogResponse): Observable<JobAttachmentViewModel> {
		const formData = new FormData();
		const formattedDate: string = DateHelper.format(new Date(), 'MM-dd-yyyy');
		const suffix: string = 'custom-chart-attachment.pdf';
		const fileName = `job-${this.job.id}-${formattedDate}-${suffix}`;

		formData.append('file', response.file);
		formData.append('jobAttachmentTypeId', '6');
		formData.append('fileName', fileName);
		formData.append('details', '...');

		return this.jobService.addJobAttachment(this.job.id, formData);
	}

	// Note: setting this function to support 'auto-gen-pressure-test' now, but
	// ready to support other settings presets later, per stakeholders request
	// during discussion about this feature.
	getChannelSettingsByChartGenerationType(chartSettings: ChartSettingsModel, type: CustomChartSettingsType): ChannelSettingsModel[] {
		const { channelSettings } = chartSettings;

		if (type === 'auto-gen-pressure-test') {
			return channelSettings.map((channelSetting: ChannelSettingsModel, index: number) => {
				const channelLabels: string[] = this.defaultChartSettings['auto-gen-pressure-test'].channelLabels;
				const channel = {
					...channelSetting,
					showGraph: channelLabels.includes(channelSetting.label),
				};

				if (index === 0) {
					channel['parent'] = true;
				} else {
					channel['combined'] = true;
					channel['linkedTo'] = 0;
				}

				return channel;
			});
		}
	}

	private init(customChartInitModel: CustomChartInitModel): Observable<any> {
		const { jobId, customChartSettingsType } = customChartInitModel;
		const chartSettings: ChartSettingsModel = this?.chartSettings || this.getCustomChartSettings(jobId, customChartSettingsType);

		this.job = this.getJobFromJobCache(jobId);
		this.jobVar.next(this.job);
		this.initJobAndChart(jobId, chartSettings);

		return of(null);
	}

	private getCustomChartSettings(jobId: number, type: CustomChartSettingsType): ChartSettingsModel {
		// If 'for-custom-charts-view', we're pulling from chart settings service.
		// Otherwise, pull default settings based on type.
		if (type === 'for-custom-charts-view') {
			return this.getDefaultChartSettingsForCustomChartsView(jobId);
		} else {
			return {
				...this.defaultChartSettings[type],
				jobId,
				channelSettings: [],
			};
		}
	}

	private buildBaseChartSettingsForCustomChartsView(jobId: number): ChartSettingsModel {
		const chartSettings: ChartSettingsModel = new ChartSettingsModel();

		chartSettings.combineUnits = false;
		chartSettings.jobId = jobId;
		chartSettings.title = 'Custom Chart';
		chartSettings.channelSettings = [];
		chartSettings.sampleDataSaturation = this.getDefaultSampleDataSaturation();

		return chartSettings;
	}

	private getJobFromJobCache(jobId: number): Job {
		return this.jobService.findJob(jobId);
	}

	initJobAndChart(jobId: number, chartSettings: ChartSettingsModel) {
		// Initialize channels.
		const userUnitPeriodChannels: UserUnitPeriodChannelDto[] = this.jobService.findUserUnitPeriodChannelsForJob(this.job.key);
		const companyChannels: UnitPeriodChannelDto[] = this.jobService.findUnitPeriodChannelsForJob(jobId);

		// Initialize start & end dates.
		let endDate: number;
		let startDate: number;
		if (this.startDate && this.endDate) {
			startDate = this.startDate.getTime();
			endDate = this.endDate.getTime();
		} else {
			endDate = null;
			startDate = this.job.start ? new Date(this.job.start).getTime() : new Date(this.job.unitPeriods[0]?.dateStarted).getTime();
		}

		forkJoin(this.getTasksForJobUnitPeriods(startDate, endDate, chartSettings))
			.pipe(takeUntil(this.destroy))
			.subscribe((unitPeriods: JobReturnCache[]) => {
				if (!chartSettings || !chartSettings?.channelSettings?.length) {
					chartSettings = this.resetOriginalChartSettings(chartSettings, companyChannels, userUnitPeriodChannels);
				}

				// Ensure there are relevant changes before rebuilding.
				if (this.shouldRebuild(unitPeriods, chartSettings, companyChannels)) {
					this.unitPeriods = unitPeriods;
					this.chartSettings = chartSettings;
					this.companyChannels = companyChannels;
					this.buildChart();
				}
			});
	}

	private shouldRebuild(unitPeriods: JobReturnCache[], chartSettings: ChartSettingsModel, companyChannels: UnitPeriodChannelDto[]): boolean {
		// Should rebuild if: unitPeriods or chartSettings, or channels have changed.
		return !UtilityHelper.isEqual(this.unitPeriods, unitPeriods) || !UtilityHelper.isEqual(this.chartSettings, chartSettings) || !UtilityHelper.isEqual(this.companyChannels, companyChannels);
	}

	private getDefaultChartSettingsForCustomChartsView(jobId: number): ChartSettingsModel {
		return this.customChartService.getUserChartSettingsByJobId(jobId) || this.buildBaseChartSettingsForCustomChartsView(jobId);
	}

	private getDefaultSampleDataSaturation(): SampleDataSaturationModel {
		const { secondSpan, saturationLimit, reductionMethod, saturationMode } = this.defaultSaturationSettings;

		return {
			secondSpan,
			saturationLimit,
			reductionMethod,
			saturationMode,
		};
	}

	private getTasksForJobUnitPeriods(startDate: number, endDate: number, chartSettings: ChartSettingsModel): Observable<JobReturnCache>[] {
		const unitPeriodIds: number[] = this.job.unitPeriods.map((up) => up.id);
		const tasksObservables: Observable<JobReturnCache>[] = [];

		unitPeriodIds.forEach((unitPeriodId: number) => {
			let secondSpan: number;
			let saturationLimit: number;
			let reductionMethod: ReductionMethod;

			if (chartSettings?.sampleDataSaturation) {
				const mode: SaturationMode = chartSettings.sampleDataSaturation.saturationMode;

				// Conditionally set saturation params based on mode.
				if (mode === 'max-points') {
					saturationLimit = chartSettings.sampleDataSaturation.saturationLimit;
					reductionMethod = chartSettings.sampleDataSaturation.reductionMethod;
				} else if (mode === 'seconds-interval') {
					secondSpan = chartSettings.sampleDataSaturation.secondSpan;
				}
			}

			tasksObservables.push(
				this.jobDataService.getJobDataSampleSaturated({
					jobKey: this.job.key,
					unitPeriodId,
					startDate,
					endDate,
					secondSpan,
					saturationLimit,
					reductionMethod,
				})
			);
		});

		return tasksObservables;
	}

	selectPointsByDrag(event): boolean {
		let dateRange = { startDate: null, endDate: null };
		if (event.xAxis) {
			let minDate = event?.xAxis[0]?.min;
			let maxDate = event?.xAxis[0]?.max;
			dateRange = { startDate: minDate, endDate: maxDate };
		}

		this.dateRange.next({ ...dateRange });

		return true;
	}
}
